import { useMemo } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { FeatureKey } from "@/lib/billing/featureGates";

// ─── Types ────────────────────────────────────────────────────────────────────

export type FeatureAccessResult = {
  /**
   * Whether the user is allowed to access this feature right now.
   * False during loading (prevents flashes of gated content).
   */
  allowed: boolean;
  /**
   * Days remaining on the free trial.
   * null when the user is not on a trial or while data is still loading.
   */
  trialDaysLeft: number | null;
  /** True while Clerk auth or the Convex query is still resolving */
  isLoading: boolean;
  /** True when the user is authenticated with Clerk */
  isSignedIn: boolean;
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * useFeatureAccess
 *
 * Convex query hook for component-level subscription gating. Wraps the
 * server-side `billing.checkFeatureAccess` query which combines the
 * entitlement check (active subscription + grace period) with the
 * feature-level tier requirement in a single round-trip.
 *
 * Returns `{ allowed, trialDaysLeft }` for use in upgrade prompts and
 * conditional rendering. This is the recommended hook for checking
 * feature access in components.
 *
 * For route-level gating, use `SubscriptionGate` in App.tsx instead.
 *
 * @param featureKey - One of the FeatureKey values from featureGates.ts
 * @returns { allowed, trialDaysLeft, isLoading, isSignedIn }
 *
 * @example
 * ```tsx
 * function CreateExtraServerButton() {
 *   const { allowed, trialDaysLeft } = useFeatureAccess("server_creation_beyond_limit");
 *   if (!allowed) {
 *     return (
 *       <FeatureUpgradePrompt
 *         featureKey="server_creation_beyond_limit"
 *         trialDaysLeft={trialDaysLeft}
 *       />
 *     );
 *   }
 *   return <button>Create Organization</button>;
 * }
 * ```
 */
export function useFeatureAccess(featureKey: FeatureKey): FeatureAccessResult {
  const { isLoaded, isSignedIn } = useAuth();

  const result = useQuery(
    api.billing.checkFeatureAccess,
    isSignedIn ? { featureKey } : "skip"
  );

  return useMemo<FeatureAccessResult>(() => {
    // Clerk not yet initialized
    if (!isLoaded) {
      return { allowed: false, trialDaysLeft: null, isLoading: true, isSignedIn: false };
    }

    // User not signed in
    if (!isSignedIn) {
      return { allowed: false, trialDaysLeft: null, isLoading: false, isSignedIn: false };
    }

    // Convex query still resolving
    if (result === undefined) {
      return { allowed: false, trialDaysLeft: null, isLoading: true, isSignedIn: true };
    }

    // Convex returned null (shouldn't happen, but guard)
    if (result === null) {
      return { allowed: false, trialDaysLeft: null, isLoading: false, isSignedIn: true };
    }

    return {
      allowed: result.allowed,
      trialDaysLeft: result.trialDaysLeft,
      isLoading: false,
      isSignedIn: true,
    };
  }, [isLoaded, isSignedIn, result]);
}
